import api from '../config/api';

class PointsService {
    /**
     * Get points configuration
     */
    async getPointsConfig() {
        try {
            const response = await api.get('/points/config');
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Update points configuration (Admin)
     */
    async updatePointsConfig(config) {
        try {
            const response = await api.put('/points/admin/config', config);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Get all buyers' points (Admin)
     */
    async getAllBuyersPoints(page = 1, limit = 20, sortBy = 'points_balance', sortOrder = 'DESC') {
        try {
            const response = await api.get('/points/admin/buyers', {
                params: { page, limit, sortBy, sortOrder }
            });
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Get specific buyer's points details (Admin)
     */
    async getBuyerPointsDetails(userId, page = 1, limit = 20) {
        try {
            const response = await api.get(`/points/admin/buyer/${userId}`, {
                params: { page, limit }
            });
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Adjust buyer's points (Admin)
     */
    async adjustBuyerPoints(data) {
        try {
            const response = await api.post('/points/admin/adjust', data);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Get points analytics (Admin)
     */
    async getPointsAnalytics() {
        try {
            const response = await api.get('/points/admin/analytics');
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Expire old points (Admin)
     */
    async expireOldPoints() {
        try {
            const response = await api.post('/points/admin/expire');
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Get points rules by type (Admin)
     * @param {string} ruleType - 'earning' or 'redemption'
     */
    async getPointsRules(ruleType) {
        try {
            const response = await api.get(`/admin/points-rules/${ruleType}`);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Create points rule (Admin)
     * @param {object} ruleData - Rule data including rule_type
     */
    async createPointsRule(ruleData) {
        try {
            const { rule_type, ...data } = ruleData;
            const response = await api.post(`/admin/points-rules/${rule_type}`, data);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Update points rule (Admin)
     * @param {number} ruleId - Rule ID
     * @param {object} ruleData - Rule data including rule_type
     */
    async updatePointsRule(ruleId, ruleData) {
        try {
            const { rule_type, ...data } = ruleData;
            const response = await api.put(`/admin/points-rules/${rule_type}/${ruleId}`, data);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Delete points rule (Admin)
     * @param {number} ruleId - Rule ID
     * @param {string} ruleType - 'earning' or 'redemption'
     */
    async deletePointsRule(ruleId, ruleType) {
        try {
            const response = await api.delete(`/admin/points-rules/${ruleType}/${ruleId}`);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    /**
     * Toggle rule active status (Admin)
     * @param {number} ruleId - Rule ID
     */
    async toggleRuleStatus(ruleId) {
        try {
            const response = await api.post(`/admin/points-rules/${ruleId}/toggle`);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    handleError(error) {
        if (error.response) {
            throw new Error(error.response.data.message || 'An error occurred');
        }
        throw new Error('Network error. Please check your connection.');
    }

    // Analytics endpoints
    async getAnalyticsDashboard(dateRange = {}) {
        try {
            const params = {};
            if (dateRange.startDate) params.startDate = dateRange.startDate;
            if (dateRange.endDate) params.endDate = dateRange.endDate;

            const response = await api.get('/admin/points-analytics/dashboard', { params });
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async getPointsOverTime(dateRange = {}, interval = 'day') {
        try {
            const params = { interval };
            if (dateRange.startDate) params.startDate = dateRange.startDate;
            if (dateRange.endDate) params.endDate = dateRange.endDate;

            const response = await api.get('/admin/points-analytics/over-time', { params });
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async getTopUsers(limit = 10, sortBy = 'earned') {
        try {
            const response = await api.get('/admin/points-analytics/top-users', {
                params: { limit, sortBy }
            });
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async getUserPoints(userId) {
        try {
            const response = await api.get(`/admin/points-analytics/user/${userId}`);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async adjustPoints(data) {
        try {
            const response = await api.post('/admin/points-analytics/adjust', data);
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    async exportReport(dateRange = {}, type = 'all') {
        try {
            const params = { type };
            if (dateRange.startDate) params.startDate = dateRange.startDate;
            if (dateRange.endDate) params.endDate = dateRange.endDate;

            const response = await api.get('/admin/points-analytics/export', {
                params,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }
}

export default new PointsService();
